import { z } from "zod";
import { ENABLE_PAST_DATE_RESCHEDULING_BYPASS } from "@/constants/appointment-rescheduling";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import type { Appointment } from "@/types/admin-portal/appointment";
import type { PREFERRED_THERAPIST_GENDER } from "../constants";
import { APPOINTMENT_SCHEDULING_SCHEMA } from "./form";

// * define the form schema
const { appointmentDateTime, preferredTherapistGender, therapist } =
	APPOINTMENT_SCHEDULING_SCHEMA.shape;

// Custom appointmentDateTime schema for reschedule that allows past dates when bypass is enabled
const RESCHEDULE_APPOINTMENT_DATE_TIME_SCHEMA =
	ENABLE_PAST_DATE_RESCHEDULING_BYPASS
		? z.coerce
				.date()
				.nullable() // Allow any date when bypass is enabled
		: appointmentDateTime.nullable(); // Use original validation when bypass is disabled

export const RESCHEDULE_APPOINTMENT_FORM_SCHEMA = z.object({
	preferredTherapistGender,
	appointmentDateTime: RESCHEDULE_APPOINTMENT_DATE_TIME_SCHEMA,
	therapist,
	reason: z.string().optional(),
	mitraAssignmentReason: z.string().optional(),
	sendEmailNotification: z.boolean().default(true),
	bypassSlotConflict: z.boolean().default(false),
	bypassContractCheck: z.boolean().default(false),
	bypassJourneyCheck: z.boolean().default(false),
	formOptions: z
		.object({
			findTherapistsAllOfDay: z.boolean().default(false),
		})
		.default({ findTherapistsAllOfDay: false }),
});
export type AppointmentRescheduleSchema = z.infer<
	typeof RESCHEDULE_APPOINTMENT_FORM_SCHEMA
>;

// * define the form default values
export const defineFormDefaultValues = (
	appointment: Appointment,
	mitraAssignmentReason?: string | null,
) => {
	const { appointmentDateTime, therapist, preferredTherapistGender } =
		appointment;

	return {
		preferredTherapistGender,
		appointmentDateTime: appointmentDateTime
			? new Date(appointmentDateTime)
			: null,
		therapist,
		reason: "",
		mitraAssignmentReason: mitraAssignmentReason ?? undefined,
		sendEmailNotification: true,
		bypassSlotConflict: false,
		bypassContractCheck: false,
		bypassJourneyCheck: false,
		formOptions: { findTherapistsAllOfDay: false },
	} satisfies AppointmentRescheduleSchema;
};

// * define the form payload
export interface AppointmentReschedulePayload {
	appointmentDateTime: Date | null;
	preferredTherapistGender: (typeof PREFERRED_THERAPIST_GENDER)[number];
	therapistId: string | null;
	reason: string | null;
	mitraAssignmentReason?: string;
	sendEmailNotification: boolean;
	bypassSlotConflict: boolean;
	bypassContractCheck: boolean;
	bypassJourneyCheck: boolean;
	isAllOfDay?: boolean;
}

export const buildPayload = (values: AppointmentRescheduleSchema) => {
	const {
		preferredTherapistGender,
		appointmentDateTime = null,
		therapist,
		reason = null,
		mitraAssignmentReason,
		sendEmailNotification = true,
		bypassSlotConflict = false,
		bypassContractCheck = false,
		bypassJourneyCheck = false,
		formOptions,
	} = values;
	const payload = deepTransformKeysToSnakeCase({
		preferredTherapistGender,
		appointmentDateTime,
		therapistId: therapist?.id ? String(therapist.id) : null,
		reason,
		// Sent only while the therapist is a Mitra: a reason chosen and then abandoned
		// by switching back to KARPIS must not land on the Order.
		mitraAssignmentReason:
			therapist?.employmentType === "FLAT" ? mitraAssignmentReason : undefined,
		sendEmailNotification,
		bypassSlotConflict,
		bypassContractCheck,
		bypassJourneyCheck,
		isAllOfDay: formOptions?.findTherapistsAllOfDay ?? false,
	} satisfies AppointmentReschedulePayload);

	return payload;
};
